import { Stack } from "@mantine/core";
import { useLocation, useNavigate } from "@tanstack/react-router";
import type { MRT_ColumnFiltersState, MRT_SortingState } from "mantine-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import type { BucketSelection } from "src/components/DocumentGraph/types";
import { useTab } from "src/contexts/tabContext";
import { TourProvider, useTour } from "src/contexts/tourContext";
import { ColumnType } from "src/db";
import { useColumns } from "src/hooks/useDatasources";
import { documentViewerTourSteps } from "./documentViewerTourSteps";
import { GuidedTour } from "./GuidedTour";
import { ActiveFiltersBar } from "./MetadataDocuments/ActiveFiltersBar";
import {
    createDateColumn,
    createStringColumn,
    getMetadataValue,
    normalizeStringArray,
} from "./MetadataDocuments/columns/columnHelpers";
import { createUrlActionsColumn } from "./MetadataDocuments/columns/linkColumnHelper";
import { useMetadataDocumentsQuery } from "./MetadataDocuments/hooks/useMetadataDocumentsQuery";
import { MetadataDocumentsTable } from "./MetadataDocuments/MetadataDocumentsTable";
import { buildChatContextFromFilters } from "./MetadataDocuments/services/buildChatContextFromFilters";
import { describeActiveFilters } from "./MetadataDocuments/services/describeActiveFilters";
import { setPendingChatContext } from "./MetadataDocuments/services/pendingChatContext";
import { seedColumnFilters } from "./MetadataDocuments/services/seedColumnFilters";
import { flattenMetadataHits } from "./MetadataDocuments/utils";
import { PdfFocusProvider } from "./PDFViewer/context";

interface SearchHitsTotal {
    value: number;
    relation: string;
}

interface SearchResponsePage {
    hits: {
        total: SearchHitsTotal;
        hits: unknown[];
    };
}

interface QueryData {
    pages?: SearchResponsePage[];
}

const MetadataDocuments = ({
    index,
    namespace,
    datasourceId,
    datasourceLabel,
    onFilteredCountChange,
    initialFilter,
}: {
    index: string;
    namespace?: string;
    datasourceId?: string;
    datasourceLabel?: string;
    onFilteredCountChange?: (count: number) => void;
    initialFilter?: BucketSelection[];
}) => {
    const [columns, setColumns] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(() =>
        seedColumnFilters({ namespace: namespace ?? index, selections: initialFilter ?? [] }),
    );
    const [globalFilter, setGlobalFilter] = useState<string>();
    const columnsQuery = useColumns(index);

    const prevIndexRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (prevIndexRef.current !== undefined && prevIndexRef.current !== index) {
            setColumnFilters([]);
            setGlobalFilter(undefined);
            setSorting([]);
        }
        prevIndexRef.current = index;
    }, [index]);

    const reconciledFilterRef = useRef(false);
    useEffect(() => {
        if (reconciledFilterRef.current) return;
        const namespaceFromColumns = columnsQuery.data?.namespace;
        if (!namespaceFromColumns || !initialFilter?.length) return;
        reconciledFilterRef.current = true;
        setColumnFilters(
            seedColumnFilters({ namespace: namespaceFromColumns, selections: initialFilter }),
        );
    }, [columnsQuery.data, initialFilter]);

    useEffect(() => {
        if (!columnsQuery.data) return;
        const namespace = columnsQuery.data.namespace;
        const urlFields: string[] = [];
        const cols = columnsQuery.data.index_description.flatMap((index) => {
            const header = index.header_name;
            const field = index.name;
            const type = index.type;
            switch (type) {
                case ColumnType.DATE:
                    return createDateColumn({ namespace, field, header });

                case ColumnType.URL:
                    urlFields.push(field);
                    return [];

                case ColumnType.BOOLEAN:
                    return createStringColumn({
                        namespace,
                        field,
                        header,
                        accessor: (row) => (getMetadataValue(row, field) ? "Yes" : "No"),
                    });

                default:
                    return createStringColumn({
                        namespace,
                        field,
                        header,
                        accessor: (row) => normalizeStringArray(getMetadataValue(row, field)),
                    });
            }
        });
        if (urlFields.length > 0) {
            cols.unshift(createUrlActionsColumn({ namespace, urlFields }));
        }
        setColumns(cols);
    }, [columnsQuery.data]);

    const { data, fetchNextPage, isFetching, isLoading, isError } = useMetadataDocumentsQuery({
        index,
        sorting,
        columnFilters,
        globalFilter,
    });

    const flatData = useMemo(() => flattenMetadataHits(data, isError), [data, isError]);

    const filteredCount = useMemo(() => {
        const typedData = data as QueryData;
        return typedData?.pages?.[0]?.hits?.total?.value ?? 0;
    }, [data]);

    const hasActiveFilters = useMemo(() => {
        return columnFilters.length > 0 || globalFilter !== undefined;
    }, [columnFilters, globalFilter]);

    useEffect(() => {
        if (onFilteredCountChange) {
            onFilteredCountChange(hasActiveFilters ? filteredCount : undefined);
        }
    }, [filteredCount, onFilteredCountChange, hasActiveFilters]);

    const { setSelectedTab } = useTab();

    useEffect(() => {
        setSelectedTab("field_service_reports");
    }, [setSelectedTab]);

    function TourConsumer() {
        const { isTourOpen, completeTour } = useTour();
        return (
            <GuidedTour
                steps={documentViewerTourSteps}
                onComplete={completeTour}
                onClose={completeTour}
                isOpen={isTourOpen}
                pathCheck={() => location.pathname.includes("/documents")}
            />
        );
    }
    const labelById = useMemo(
        () =>
            new Map(
                (columns as { id?: string; header?: unknown }[]).map((column) => [
                    column.id ?? "",
                    typeof column.header === "string" ? column.header : "",
                ]),
            ),
        [columns],
    );

    const askAiAboutFilters = () => {
        setPendingChatContext({
            ...buildChatContextFromFilters({ columnFilters, labelById }),
            datasources: datasourceId ? [datasourceId] : [],
            datasourceLabel: datasourceLabel ?? "",
        });
        navigate({ to: "/" });
    };

    const removeFilter = (id: string) =>
        setColumnFilters((prev) => prev.filter((filter) => filter.id !== id));

    return (
        <TourProvider tourId="docs-tour">
            <TourConsumer />
            <PdfFocusProvider>
                <Stack gap="sm">
                    <ActiveFiltersBar
                        filters={describeActiveFilters({ columnFilters, labelById })}
                        onRemove={removeFilter}
                        onAskAi={askAiAboutFilters}
                    />
                    <MetadataDocumentsTable
                        index={index}
                        columns={columns}
                        data={flatData}
                        queryData={data}
                        isLoading={isLoading}
                        isFetching={isFetching}
                        sorting={sorting}
                        setSorting={setSorting}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        globalFilter={globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        fetchNextPage={fetchNextPage}
                    />
                </Stack>
            </PdfFocusProvider>
        </TourProvider>
    );
};

export default MetadataDocuments;
